import { useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { WalletIcon, BrainIcon, CalendarIcon, TrendingUpIcon } from "@/components/icons/LuxuryIcons";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const GlowAppCinematicScroll = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  // Background gradient transform
  const backgroundOpacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.1, 0.2, 0.3, 0.1]);
  
  return (
    <section ref={containerRef} className="relative bg-white overflow-hidden">
      {/* Animated Background - changed to subtle white overlay */}
      <motion.div 
        className="fixed inset-0 bg-juv-soft-blush/10 pointer-events-none" 
        style={{
          opacity: backgroundOpacity
        }} 
      />

      {/* Feature Sections */}
      <WalletSection />
      <SmartQuizSection />
      <BookingSection />
      <ProgressSection />
      <FinalCTASection />
    </section>
  );
};

// Section 1: JUV Wallet
const WalletSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, {
    amount: 0.3
  });
  
  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 md:px-6 py-20 bg-white">
      <motion.div ref={sectionRef} className="container max-w-6xl mx-auto" initial={{
      opacity: 0
    }} animate={{
      opacity: isInView ? 1 : 0.3
    }} transition={{
      duration: 0.8,
      ease: "easeOut"
    }}>
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Phone Mockup */}
          <motion.div className="relative mx-auto w-64 h-[480px] md:w-80 md:h-[640px] bg-black rounded-[2rem] md:rounded-[3rem] p-4 md:p-6 shadow-2xl order-2 lg:order-1" initial={{
          y: 100,
          opacity: 0
        }} animate={isInView ? {
          y: 0,
          opacity: 1
        } : {
          y: 100,
          opacity: 0
        }} transition={{
          duration: 1,
          delay: 0.2,
          ease: "easeOut"
        }}>
            <div className="w-full h-full bg-gradient-to-br from-juv-cream-white to-juv-soft-blush rounded-[1.5rem] md:rounded-[2rem] overflow-hidden relative">
              <div className="p-6 md:p-8 h-full flex flex-col justify-center items-center">
                <motion.div initial={{
                scale: 0
              }} animate={isInView ? {
                scale: 1
              } : {
                scale: 0
              }} transition={{
                duration: 0.6,
                delay: 0.5,
                ease: "backOut"
              }}>
                  <WalletIcon size={60} className="text-juv-terracotta mb-4 md:mb-6" />
                </motion.div>
                
                <motion.div className="text-center mb-6 md:mb-8" initial={{
                y: 30,
                opacity: 0
              }} animate={isInView ? {
                y: 0,
                opacity: 1
              } : {
                y: 30,
                opacity: 0
              }} transition={{
                duration: 0.8,
                delay: 0.7
              }}>
                  <CreditCounter isVisible={isInView} />
                  <p className="text-juv-deep-chestnut/70 mt-2 text-sm">Available Credits</p>
                </motion.div>

                {/* Floating Credits Animation */}
                {isInView && <div className="absolute inset-0 pointer-events-none">
                    {[...Array(6)].map((_, i) => <FloatingCredit key={i} delay={1 + i * 0.2} />)}
                  </div>}
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div className="text-center lg:text-left order-1 lg:order-2" initial={{
          x: 50,
          opacity: 0
        }} animate={isInView ? {
          x: 0,
          opacity: 1
        } : {
          x: 50,
          opacity: 0
        }} transition={{
          duration: 1,
          delay: 0.3
        }}>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-juv-deep-chestnut mb-6 md:mb-8">
              Your JUV Wallet
            </h2>
            <p className="text-lg md:text-xl text-juv-deep-chestnut/70 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              Track your credits, rewards, and savings. Your beauty investments, 
              all in one place with transparent value tracking.
            </p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

// Section 2: Smart Quiz
const SmartQuizSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, {
    amount: 0.3
  });
  const concerns = ["Acne", "TMJ", "Whitening", "Lines", "Glow"];
  
  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 md:px-6 py-20 bg-juv-soft-blush/20">
      <motion.div ref={sectionRef} className="container max-w-6xl mx-auto" initial={{
      opacity: 0
    }} animate={{
      opacity: isInView ? 1 : 0.3
    }} transition={{
      duration: 0.8
    }}>
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          <motion.div className="text-center lg:text-left" initial={{
          x: -50,
          opacity: 0
        }} animate={isInView ? {
          x: 0,
          opacity: 1
        } : {
          x: -50,
          opacity: 0
        }} transition={{
          duration: 1,
          delay: 0.2
        }}>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-juv-deep-chestnut mb-6 md:mb-8">
              Smart Quiz
            </h2>
            <p className="text-lg md:text-xl text-juv-deep-chestnut/70 leading-relaxed mb-4 md:mb-6 max-w-2xl mx-auto lg:mx-0">
              AI-powered treatment recommendations. Your path to glow starts with personalization.
            </p>
            <p className="text-base md:text-lg text-juv-deep-chestnut/60 max-w-2xl mx-auto lg:mx-0">
              Answer a few questions and let our AI match you with the perfect treatments.
            </p>
          </motion.div>

          {/* Quiz Animation */}
          <div className="relative h-80 md:h-96 flex items-center justify-center">
            <motion.div className="relative w-64 h-64 md:w-80 md:h-80 rounded-full border-2 border-juv-terracotta/30 flex items-center justify-center" initial={{
            scale: 0
          }} animate={isInView ? {
            scale: 1
          } : {
            scale: 0
          }} transition={{
            duration: 0.8,
            delay: 0.3
          }}>
              <BrainIcon size={48} className="text-juv-terracotta" />
              
              {/* Floating Concern Bubbles */}
              {concerns.map((concern, index) => {
              const angle = index * 2 * Math.PI / concerns.length;
              const radius = 100;
              const x = Math.cos(angle) * radius;
              const y = Math.sin(angle) * radius;
              return <motion.div key={concern} className="absolute bg-white rounded-full px-3 py-2 md:px-4 md:py-2 shadow-lg border border-juv-rose-clay/20" style={{
                left: `calc(50% + ${x}px)`,
                top: `calc(50% + ${y}px)`,
                transform: 'translate(-50%, -50%)'
              }} initial={{
                scale: 0,
                opacity: 0
              }} animate={isInView ? {
                scale: 1,
                opacity: 1
              } : {
                scale: 0,
                opacity: 0
              }} transition={{
                duration: 0.5,
                delay: 0.5 + index * 0.1,
                ease: "backOut"
              }} whileHover={{
                scale: 1.1
              }}>
                    <span className="text-xs md:text-sm font-medium text-juv-deep-chestnut whitespace-nowrap">{concern}</span>
                  </motion.div>;
            })}
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Section 3: Instant Booking
const BookingSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, {
    amount: 0.3
  });
  
  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 md:px-6 py-20">
      <motion.div ref={sectionRef} className="container max-w-6xl mx-auto" initial={{
      opacity: 0
    }} animate={{
      opacity: isInView ? 1 : 0.3
    }} transition={{
      duration: 0.8
    }}>
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Calendar Animation */}
          <div className="relative h-80 md:h-96 flex items-center justify-center order-2 lg:order-1">
            <motion.div className="bg-white rounded-2xl shadow-2xl p-4 md:p-6 w-full max-w-sm" initial={{
            y: 100,
            opacity: 0
          }} animate={isInView ? {
            y: 0,
            opacity: 1
          } : {
            y: 100,
            opacity: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }}>
              <div className="flex items-center mb-4">
                <CalendarIcon size={20} className="text-juv-terracotta mr-3" />
                <h3 className="font-semibold text-juv-deep-chestnut text-sm md:text-base">Book Appointment</h3>
              </div>
              
              <div className="grid grid-cols-3 gap-2 mb-4">
                {["9:00", "11:30", "2:00", "3:30", "5:00", "6:30"].map((time, index) => <motion.button key={time} className={`p-2 md:p-3 rounded-lg text-xs md:text-sm font-medium transition-colors ${index === 1 ? 'bg-juv-terracotta text-white' : 'bg-juv-soft-blush text-juv-deep-chestnut hover:bg-juv-rose-clay/30'}`} initial={{
                scale: 0
              }} animate={isInView ? {
                scale: 1
              } : {
                scale: 0
              }} transition={{
                duration: 0.3,
                delay: 0.5 + index * 0.1
              }} whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }}>
                    {time}
                  </motion.button>)}
              </div>
              
              <motion.div initial={{
              scale: 0
            }} animate={isInView ? {
              scale: 1
            } : {
              scale: 0
            }} transition={{
              duration: 0.5,
              delay: 1.2
            }} className="text-center">
                <div className="w-6 h-6 md:w-8 md:h-8 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-2">
                  <svg className="w-3 h-3 md:w-5 md:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-xs md:text-sm text-juv-deep-chestnut">Confirmed!</p>
              </motion.div>
            </motion.div>
          </div>

          <motion.div className="text-center lg:text-left order-1 lg:order-2" initial={{
          x: 50,
          opacity: 0
        }} animate={isInView ? {
          x: 0,
          opacity: 1
        } : {
          x: 50,
          opacity: 0
        }} transition={{
          duration: 1,
          delay: 0.3
        }}>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-juv-deep-chestnut mb-6 md:mb-8">
              Instant Booking
            </h2>
            <p className="text-lg md:text-xl text-juv-deep-chestnut/70 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              Same-day appointments at your fingertips. Your schedule, your treatments, 
              your convenience.
            </p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

// Section 4: Progress Tracking
const ProgressSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, {
    amount: 0.3
  });
  const milestones = [
    {
      label: "First Visit",
      icon: "🌟",
      completed: true
    },
    {
      label: "Facial Treatment", 
      icon: "✨",
      completed: true
    },
    {
      label: "Laser Whitening",
      icon: "⚡",
      completed: true
    },
    {
      label: "TMJ Relief",
      icon: "💆",
      completed: false
    }
  ];
  
  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 md:px-6 py-20 bg-juv-soft-blush/10">
      <motion.div ref={sectionRef} className="container max-w-6xl mx-auto" initial={{
      opacity: 0
    }} animate={{
      opacity: isInView ? 1 : 0.3
    }} transition={{
      duration: 0.8
    }}>
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          <motion.div className="text-center lg:text-left" initial={{
          x: -50,
          opacity: 0
        }} animate={isInView ? {
          x: 0,
          opacity: 1
        } : {
          x: -50,
          opacity: 0
        }} transition={{
          duration: 1,
          delay: 0.2
        }}>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-juv-deep-chestnut mb-6 md:mb-8">
              Progress Tracking
            </h2>
            <p className="text-lg md:text-xl text-juv-deep-chestnut/70 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              Watch your glow journey unfold. Track treatments, see results, 
              and celebrate your transformation.
            </p>
          </motion.div>

          {/* Progress Timeline */}
          <div className="relative">
            <TrendingUpIcon size={48} className="text-juv-terracotta mb-6 md:mb-8 mx-auto lg:mx-0" />
            
            <div className="space-y-4 md:space-y-6">
              {milestones.map((milestone, index) => <motion.div key={milestone.label} className="flex items-center space-x-3 md:space-x-4" initial={{
              x: 50,
              opacity: 0
            }} animate={isInView ? {
              x: 0,
              opacity: 1
            } : {
              x: 50,
              opacity: 0
            }} transition={{
              duration: 0.6,
              delay: 0.3 + index * 0.1
            }}>
                  <motion.div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center ${milestone.completed ? 'bg-juv-terracotta text-white' : 'bg-juv-soft-blush text-juv-deep-chestnut'}`} initial={{
                scale: 0
              }} animate={isInView ? {
                scale: 1
              } : {
                scale: 0
              }} transition={{
                duration: 0.4,
                delay: 0.5 + index * 0.1,
                ease: "backOut"
              }}>
                    <span className="text-sm md:text-lg">{milestone.icon}</span>
                  </motion.div>
                  
                  <div className="flex-1">
                    <div className={`h-2 rounded-full ${milestone.completed ? 'bg-juv-terracotta' : 'bg-juv-soft-blush'}`}>
                      <motion.div className="h-full bg-juv-terracotta rounded-full" initial={{
                    width: 0
                  }} animate={isInView && milestone.completed ? {
                    width: "100%"
                  } : {
                    width: 0
                  }} transition={{
                    duration: 0.8,
                    delay: 0.7 + index * 0.1
                  }} />
                    </div>
                    <p className="text-sm md:text-base text-juv-deep-chestnut mt-1">{milestone.label}</p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Final CTA Section
const FinalCTASection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, {
    amount: 0.3
  });
  
  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 md:px-6 py-20 bg-juv-deep-chestnut text-white">
      <motion.div ref={sectionRef} className="container max-w-4xl mx-auto text-center" initial={{
      opacity: 0
    }} animate={{
      opacity: isInView ? 1 : 0.3
    }} transition={{
      duration: 0.8
    }}>
        <motion.h2 initial={{
        y: 50,
        opacity: 0
      }} animate={isInView ? {
        y: 0,
        opacity: 1
      } : {
        y: 50,
        opacity: 0
      }} transition={{
        duration: 1,
        delay: 0.2
      }} className="text-4xl md:text-6xl lg:text-7xl font-serif font-light mb-6 md:mb-8 text-white">
          Your Glow Journey Starts Here
        </motion.h2>
        
        <motion.p className="text-lg md:text-xl text-white/80 mb-12 md:mb-16 max-w-2xl mx-auto leading-relaxed" initial={{
        y: 30,
        opacity: 0
      }} animate={isInView ? {
        y: 0,
        opacity: 1
      } : {
        y: 30,
        opacity: 0
      }} transition={{
        duration: 0.8,
        delay: 0.4
      }}>
          Download the app and discover personalized treatments designed just for you.
        </motion.p>
        
        <motion.div className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center" initial={{
        y: 30,
        opacity: 0
      }} animate={isInView ? {
        y: 0,
        opacity: 1
      } : {
        y: 30,
        opacity: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }}>
          <Button asChild size="lg" className="px-6 md:px-8 py-3 md:py-4 rounded-full font-medium text-base md:text-lg hover:scale-105 transition-all duration-300 bg-white text-juv-deep-chestnut hover:bg-white/90">
            <Link to="/quiz">Take the Quiz</Link>
          </Button>
          
          <Button asChild size="lg" className="px-6 md:px-8 py-3 md:py-4 rounded-full font-medium text-base md:text-lg border border-white/30 text-white bg-transparent hover:bg-white/10 hover:scale-105 transition-all duration-300">
            <Link to="/treatments">Explore Treatments</Link>
          </Button>
        </motion.div>
      </motion.div>
    </div>
  );
};

// Helper Components
const CreditCounter = ({
  isVisible
}: {
  isVisible: boolean;
}) => {
  return (
    <motion.div 
      className="text-2xl md:text-3xl font-bold text-juv-deep-chestnut" 
      initial={{
        scale: 0
      }} 
      animate={isVisible ? {
        scale: 1
      } : {
        scale: 0
      }} 
      transition={{
        duration: 0.8,
        delay: 0.8
      }}
    >
      <motion.span 
        initial={{
          opacity: 0
        }} 
        animate={isVisible ? {
          opacity: 1
        } : {
          opacity: 0
        }} 
        transition={{
          duration: 2,
          delay: 1
        }}
      >
        12 Credits
      </motion.span>
      <div className="text-base md:text-lg text-juv-terracotta">$600 Value</div>
    </motion.div>
  );
};

const FloatingCredit = ({
  delay
}: {
  delay: number;
}) => {
  return (
    <motion.div 
      className="absolute w-4 h-4 md:w-6 md:h-6 bg-juv-terracotta rounded-full flex items-center justify-center text-white text-xs font-bold" 
      initial={{
        x: Math.random() * 200 + 50,
        y: Math.random() * 300 + 100,
        scale: 0,
        opacity: 0
      }} 
      animate={{
        x: 120,
        y: 180,
        scale: [0, 1, 0],
        opacity: [0, 1, 0]
      }} 
      transition={{
        duration: 1.5,
        delay,
        ease: "easeInOut"
      }}
    >
      $
    </motion.div>
  );
};

export default GlowAppCinematicScroll;
